import { forwardRef } from 'react';
import { Ruler, Zap, Users, Calendar, MapPin } from 'lucide-react';

interface LocationSlideTemplateProps {
  location: {
    locode: string | null;
    name: string;
    city: string | null;
    ownership_type: string | null;
    surface_area_m2: number | null;
    contracted_power_kw: number | null;
    total_investment: number | null;
    equity_percentage: number | null;
    cost_land_purchase: number | null;
    cost_land_development: number | null;
    cost_engineering: number | null;
    cost_charging_infra: number | null;
    notes: string | null;
    latitude: number | null;
    longitude: number | null;
    target_quarter?: string | null;
  };
  interestedParties: Array<{
    orgName: string;
    orgLogo?: string | null;
  }>;
  siteDrawingUrl?: string | null;
  mapboxToken?: string | null;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `€${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `€${(value / 1000).toFixed(0)}K`;
  }
  return `€${value.toFixed(0)}`;
};

const formatOwnershipType = (type: string | null) => {
  const labels: Record<string, string> = {
    'koop': 'Koop',
    'huur': 'Huur',
    'huur_koopoptie': 'Huur met koopoptie',
    'in_eigendom': 'In eigendom',
  };
  return labels[type || ''] || 'Onbekend';
};

const formatPower = (kw: number | null) => {
  if (!kw) return '-';
  if (kw >= 1000) {
    return `${(kw / 1000).toFixed(1)} MW`;
  }
  return `${kw} kW`;
};

export const LocationSlideTemplate = forwardRef<HTMLDivElement, LocationSlideTemplateProps>(
  ({ location, interestedParties, siteDrawingUrl, mapboxToken }, ref) => {
    // Calculate costs
    const costLand = location.cost_land_purchase || 0;
    const costDevelopment = location.cost_land_development || 0;
    const costEng = location.cost_engineering || 0;
    const costInfra = location.cost_charging_infra || 0;
    const totalCosts = costLand + costDevelopment + costEng + costInfra;
    
    // Calculate equity - equity_percentage applies to land, 100% for the rest
    const equityOnLand = costLand * ((location.equity_percentage || 0) / 100);
    const equityOnRest = costDevelopment + costEng + costInfra; // 100% equity on these
    const equityAmount = equityOnLand + equityOnRest;

    // Cost breakdown data
    const costData = [
      { name: 'Grond', value: costLand, color: '#00d4ff' },
      { name: 'Aanleg', value: costDevelopment, color: '#64748b' },
      { name: 'Engineering', value: costEng, color: '#9333ea' },
      { name: 'Laadinfra', value: costInfra, color: '#1e3a5f' },
    ].filter(c => c.value > 0);

    // Party display - show up to 6 favicons horizontally
    const displayParties = interestedParties.slice(0, 6);
    const remainingCount = interestedParties.length > 6 ? interestedParties.length - 6 : 0;

    // Mapbox static map URL
    const mapboxStaticUrl = location.latitude && location.longitude && mapboxToken
      ? `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+00d4ff(${location.longitude},${location.latitude})/${location.longitude},${location.latitude},13,0/880x460@2x?access_token=${mapboxToken}`
      : null;

    return (
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={{
          width: '1920px',
          height: '1080px',
          backgroundColor: '#080c55',
          fontFamily: 'Inter, system-ui, sans-serif',
          border: '4px solid #00d4ff',
          borderRadius: '20px',
          padding: '32px',
          boxSizing: 'border-box',
        }}
      >
        {/* Header Section */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '70px',
            marginBottom: '24px',
          }}
        >
          {/* LOCODE Badge */}
            <div
              style={{
                backgroundColor: '#00d4ff',
                color: '#080c55',
                fontSize: '28px',
                fontWeight: 700,
                padding: '12px 24px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '52px',
                lineHeight: '28px',
              }}
            >
            {location.locode || 'N/A'}
          </div>

          {/* Location Title */}
          <div style={{ flex: 1, marginLeft: '24px', marginRight: '24px' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '22px' }}>Locatie: </span>
            <span style={{ color: 'white', fontSize: '32px', fontWeight: 600 }}>
              {location.name}{location.city ? `, ${location.city}` : ''}
            </span>
          </div>

          {/* Ownership Badge */}
              <div
                style={{
                  backgroundColor: '#00d4ff',
                  color: '#080c55',
                  fontSize: '22px',
                  fontWeight: 700,
                  padding: '12px 28px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '52px',
                  lineHeight: '22px',
                }}
              >
            {formatOwnershipType(location.ownership_type)}
          </div>
        </div>

        {/* Main Content: Drawing + Map */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            height: '460px',
            marginBottom: '24px',
          }}
        >
          {/* Left: Site Drawing */}
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: siteDrawingUrl ? '16px' : '0',
            }}
          >
            {siteDrawingUrl ? (
              <img
                src={siteDrawingUrl}
                alt="Site Layout"
                crossOrigin="anonymous"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '20px' }}>
                Geen tekening beschikbaar
              </div>
            )}
          </div>

          {/* Right: Mapbox Map */}
          <div
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundColor: 'rgba(255,255,255,0.1)',
            }}
          >
            {mapboxStaticUrl ? (
              <img
                src={mapboxStaticUrl}
                alt="Location Map"
                crossOrigin="anonymous"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '20px',
                }}
              >
                {!mapboxToken ? 'Mapbox token niet geconfigureerd' : 'Geen coördinaten beschikbaar'}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Stats Section */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '340px 1fr 340px',
            gap: '32px',
            padding: '28px',
            border: '2px solid #00d4ff',
            borderRadius: '16px',
            height: '340px',
          }}
        >
          {/* Left Column: Key Stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Oppervlakte */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Ruler style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '4px' }}>
                  Oppervlakte:
                </div>
                <div style={{ color: 'white', fontSize: '22px', fontWeight: 700 }}>
                  {location.surface_area_m2 
                    ? `${location.surface_area_m2.toLocaleString('nl-NL')} m²`
                    : '-'}
                </div>
              </div>
            </div>

            {/* Stroomaansluiting */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Zap style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '4px' }}>
                  Stroomaansluiting:
                </div>
                <div style={{ color: 'white', fontSize: '22px', fontWeight: 700 }}>
                  {formatPower(location.contracted_power_kw)}
                </div>
              </div>
            </div>

            {/* Potentiële afnemers */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Users style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '8px' }}>
                  Potentiële afnemers:
                </div>
                {/* Horizontal favicon-only layout for up to 6 parties */}
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '10px', alignItems: 'center' }}>
                  {displayParties.length > 0 ? (
                    <>
                      {displayParties.map((party, idx) => (
                        <div
                          key={idx}
                          title={party.orgName}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            overflow: 'hidden',
                          }}
                        >
                          {party.orgLogo ? (
                            <img
                              src={party.orgLogo}
                              alt=""
                              crossOrigin="anonymous"
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Users style={{ width: '18px', height: '18px', color: 'rgba(255,255,255,0.6)' }} />
                          )}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '13px',
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          +{remainingCount}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                      Nog geen partijen
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Timing */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Calendar style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', marginBottom: '4px' }}>
                  Timing:
                </div>
                <div style={{ color: 'white', fontSize: '22px', fontWeight: 700 }}>
                  {location.target_quarter || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Middle Column: Investment Breakdown */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Total Investment */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '18px', marginBottom: '4px' }}>
                Totale investering:
              </div>
              <div style={{ color: 'white', fontSize: '42px', fontWeight: 700 }}>
                € {(location.total_investment || totalCosts).toLocaleString('nl-NL')},-
              </div>
            </div>

            {/* Legend */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '32px',
                marginBottom: '16px',
              }}
            >
              {costData.map((item) => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                    }}
                  />
                  <span style={{ color: 'white', fontSize: '16px' }}>{item.name}</span>
                </div>
              ))}
            </div>

            {/* Stacked Horizontal Bar */}
            {totalCosts > 0 && (
              <div
                style={{
                  display: 'flex',
                  height: '56px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                }}
              >
              {costData.map((item) => (
                  <div
                    key={item.name}
                    style={{
                      width: `${(item.value / totalCosts) * 100}%`,
                      height: '56px',
                      backgroundColor: item.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color === '#00d4ff' ? '#080c55' : 'white',
                      fontSize: '18px',
                      fontWeight: 700,
                      lineHeight: '56px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span style={{ display: 'block', lineHeight: '56px' }}>
                      {item.value >= totalCosts * 0.1 ? formatCurrency(item.value) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Cost Details */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '40px',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '15px',
              }}
            >
              {costData.map((item) => (
                <div key={item.name}>
                  {item.name}: <span style={{ color: 'white', fontWeight: 600 }}>{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Equity Box */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Equity Badge */}
            <div
              style={{
                backgroundColor: '#00d4ff',
                color: '#080c55',
                padding: '28px 32px',
                borderRadius: '16px',
                textAlign: 'center',
                border: '4px solid #1e3a5f',
                width: '100%',
                lineHeight: '1.3',
              }}
            >
              <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', lineHeight: '1.2' }}>
                EQUITY BEHOEFTE
              </div>
              <div style={{ fontSize: '40px', fontWeight: 900, lineHeight: '1.2' }}>
                € {(equityAmount / 1000000).toFixed(2)} MLN
              </div>
            </div>

            {/* Equity Details */}
            <div
              style={{
                marginTop: '16px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '14px',
                lineHeight: 1.6,
              }}
            >
              <div>Equity op grond: {location.equity_percentage || 0}%</div>
              <div>Equity op aanleg + laadinfra: 100%</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            position: 'absolute',
            bottom: '24px',
            left: '32px',
            right: '32px',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          {/* Notes */}
          <div style={{ flex: 1, maxWidth: '70%' }}>
            {location.notes && (
              <div
                style={{
                  color: '#00d4ff',
                  fontSize: '16px',
                  lineHeight: '24px',
                  paddingTop: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minHeight: '28px',
                }}
              >
                <span style={{ fontWeight: 600 }}>Opmerkingen:</span> {location.notes}
              </div>
            )}
          </div>

          {/* LOCODE + Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.5)' }}>
              <MapPin style={{ width: '18px', height: '18px' }} />
              <span style={{ fontSize: '14px' }}>{location.locode}</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#00d4ff', fontSize: '24px', fontWeight: 700 }}>GREENVISION</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Charging Infrastructure</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

LocationSlideTemplate.displayName = 'LocationSlideTemplate';
